import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Spacing from '../Spacing';
import { pageTitle } from '../../helpers/PageTitle';
import { userAPI } from '../../services/api';

const validationSchema = Yup.object({
  name: Yup.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters')
    .required('Full name is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  mobile: Yup.string()
    .matches(/^[6-9]\d{9}$/, 'Invalid mobile number')
    .required('Mobile number is required'),
  address: Yup.string()
    .min(10, 'Address must be at least 10 characters')
    .max(255, 'Address must be less than 255 characters')
    .required('Address is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .max(50, 'Password must be less than 50 characters')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain at least one uppercase letter, one lowercase letter, and one number')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required')
});

export default function SignUpPage() {
  pageTitle('Sign Up - Ayumedix');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setLoading(true);
    try {
      const userData = {
        fullName: values.name,
        emailID: values.email,
        mobileNo: values.mobile,
        address: values.address,
        password: values.password
      };
      
      const response = await userAPI.register(userData);
      
      if (response.data.statusCode === 200) {
        toast.success('Registration successful! Redirecting to login...', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setTimeout(() => navigate('/signin'), 1000);
      } else {
        toast.error(response.data.message || 'Registration failed');
      }
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <>
      <Spacing lg="70" md="70" />
      <section className="cs_primary_bg">
        <Spacing lg="100" md="80" />
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8">
              <div className="cs_signup_form_wrap" style={{
                background: 'rgba(0, 0, 0, 0.8)',
                borderRadius: '15px',
                padding: '40px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div className="cs_signup_form">
                  <div className="text-center mb-4">
                    <img src="/images/ayumedix.png" width={150} alt="Ayumedix Logo" />
                    <h2 className="cs_fs_40 cs_semibold mt-3 cs_white_color">Create Account</h2>
                    <p className="cs_fs_16 cs_white_color">Join Ayumedix for premium wellness products</p>
                  </div>
                  
                  <Formik
                    initialValues={{
                      name: '',
                      email: '',
                      mobile: '',
                      address: '',
                      password: '',
                      confirmPassword: ''
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ isSubmitting }) => (
                      <Form>
                        <div className="row">
                          <div className="col-12">
                            <label className="cs_fs_18 cs_semibold cs_white_color">
                              Full Name *
                            </label>
                            <Field
                              type="text"
                              name="name"
                              placeholder="Enter your full name"
                              className="cs_form_field"
                            />
                            <ErrorMessage name="name" component="div" style={{color: '#ff6b6b', fontSize: '14px', marginTop: '5px'}} />
                            <Spacing lg="25" md="20" />
                          </div>
                          
                          <div className="col-12">
                            <label className="cs_fs_18 cs_semibold cs_white_color">
                              Email Address *
                            </label>
                            <Field
                              type="email"
                              name="email"
                              placeholder="Enter your email address"
                              className="cs_form_field"
                            />
                            <ErrorMessage name="email" component="div" style={{color: '#ff6b6b', fontSize: '14px', marginTop: '5px'}} />
                            <Spacing lg="25" md="20" />
                          </div>
                          
                          <div className="col-12">
                            <label className="cs_fs_18 cs_semibold cs_white_color">
                              Mobile Number *
                            </label>
                            <Field
                              type="tel"
                              name="mobile"
                              placeholder="Enter your mobile number"
                              className="cs_form_field"
                            />
                            <ErrorMessage name="mobile" component="div" style={{color: '#ff6b6b', fontSize: '14px', marginTop: '5px'}} />
                            <Spacing lg="25" md="20" />
                          </div>
                          
                          <div className="col-12">
                            <label className="cs_fs_18 cs_semibold cs_white_color">
                              Address *
                            </label>
                            <Field
                              as="textarea"
                              name="address"
                              placeholder="Enter your complete address"
                              className="cs_form_field"
                              rows={3}
                            />
                            <ErrorMessage name="address" component="div" style={{color: '#ff6b6b', fontSize: '14px', marginTop: '5px'}} />
                            <Spacing lg="25" md="20" />
                          </div>
                          
                          <div className="col-md-6">
                            <label className="cs_fs_18 cs_semibold cs_white_color">
                              Password *
                            </label>
                            <Field
                              type="password"
                              name="password"
                              placeholder="Create password"
                              className="cs_form_field"
                            />
                            <ErrorMessage name="password" component="div" style={{color: '#ff6b6b', fontSize: '14px', marginTop: '5px'}} />
                            <Spacing lg="25" md="20" />
                          </div>
                          
                          <div className="col-md-6">
                            <label className="cs_fs_18 cs_semibold cs_white_color">
                              Confirm Password *
                            </label>
                            <Field
                              type="password"
                              name="confirmPassword"
                              placeholder="Confirm password"
                              className="cs_form_field"
                            />
                            <ErrorMessage name="confirmPassword" component="div" style={{color: '#ff6b6b', fontSize: '14px', marginTop: '5px'}} />
                            <Spacing lg="25" md="20" />
                          </div>
                          
                          <div className="col-12">
                            <div className="cs_checkbox_wrap">
                              <input type="checkbox" id="terms" required />
                              <label htmlFor="terms" className="cs_fs_14 cs_white_color">
                                I agree to the <Link to="/terms-conditions" className="cs_accent_color">Terms & Conditions</Link>
                              </label>
                            </div>
                            <Spacing lg="30" md="25" />
                          </div>
                          
                          <div className="col-12 text-center">
                            <button 
                              type="submit" 
                              className="cs_btn cs_style_1 cs_fs_16"
                              disabled={isSubmitting || loading}
                            >
                              {loading ? 'Creating Account...' : 'Create Account'}
                              <span>
                                <i><Icon icon="fa6-solid:arrow-right" /></i>
                                <i><Icon icon="fa6-solid:arrow-right" /></i>
                              </span>
                            </button>
                            <Spacing lg="20" md="15" />
                            <p className="cs_fs_14 cs_white_color">
                              Already have an account? 
                              <Link to="/signin" className="cs_accent_color cs_semibold"> Sign In</Link>
                            </p>
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Spacing lg="100" md="80" />
      </section>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </>
  );
}